/**
 * MailshotStatusBadge Component
 *
 * Displays mailshot status for a scrape with smart labeling:
 * - "All" if all properties are in mailshots
 * - "5 SSTC" if only SSTC properties
 * - "10 (5 SSTC)" if mixed (For Sale + SSTC)
 * - Clickable to show which mailshots
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaInfoCircle } from "react-icons/fa";
import { Modal } from "./Modal";
import { Badge } from "./Badge";
import { PropertyRepository } from "../../models/PropertyRepository";
import { db, functions } from "../../firebase";
import "./MailshotStatusBadge.scss";

export const MailshotStatusBadge = ({ scrape, mailshots = [] }) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const [mailshotBreakdown, setMailshotBreakdown] = useState({});
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  // PropertyRepository for read access — keeps this component off direct Firestore
  // imports (architecture rule). Stable across renders.
  const propertyRepository = useMemo(
    () => new PropertyRepository(db, functions),
    []
  );

  // Fetch per-mailshot breakdown when modal opens
  useEffect(() => {
    const fetchMailshotBreakdown = async () => {
      if (!showDetails || !scrape.mailshotStats?.mailshots) return;

      setLoadingBreakdown(true);
      try {
        // One read for the whole scrape, then count per mailshot client-side.
        // Previously this ran a separate Firestore query per mailshot (N+1).
        const properties = await propertyRepository.findByScrapeId(scrape.id);

        const breakdown = {};
        for (const mailshotId of scrape.mailshotStats.mailshots) {
          let total = 0;
          let forSale = 0;
          let underOffer = 0;
          let sstc = 0;

          properties.forEach((property) => {
            if (!property.mailshotIds?.includes(mailshotId)) return;
            total++;
            if (property.propertyStatus === "For Sale") {
              forSale++;
            } else if (property.propertyStatus === "Under Offer") {
              underOffer++;
            } else if (property.propertyStatus === "Sold STC") {
              sstc++;
            }
          });

          breakdown[mailshotId] = { total, forSale, underOffer, sstc };
        }

        setMailshotBreakdown(breakdown);
      } catch (error) {
        console.error("Error fetching mailshot breakdown:", error);
      } finally {
        setLoadingBreakdown(false);
      }
    };

    fetchMailshotBreakdown();
  }, [showDetails, scrape.id, scrape.mailshotStats?.mailshots, propertyRepository]);

  // If no mailshot stats, don't show anything
  if (!scrape.mailshotStats || scrape.mailshotStats.inMailshots === 0) {
    return (
      <Badge
        variant="default"
        size="small"
        className="mailshot-status-badge-none"
      >
        —
      </Badge>
    );
  }

  const { mailshotStats } = scrape;
  const totalProperties = scrape.propertiesFound || scrape.totalProperties || 0;
  const inMailshots = mailshotStats.inMailshots || 0;
  const forSale = mailshotStats.forSale || 0;
  // Under Offer: its own listing status from 28 Sep 2026 — never counted as SSTC.
  const underOffer = mailshotStats.underOffer || 0;
  const sstc = mailshotStats.sstc || 0;

  // Determine label and variant
  let label = "";
  let variant = "default";

  if (inMailshots >= totalProperties) {
    // All properties are in mailshots
    label = "All";
    variant = "success"; // Green
  } else if (sstc > 0 && forSale === 0 && underOffer === 0) {
    // Only SSTC properties
    label = `${sstc} SSTC`;
    variant = "warning"; // Orange
  } else if (underOffer > 0 && forSale === 0 && sstc === 0) {
    // Only Under Offer properties
    label = `${underOffer} UO`;
    variant = "purple";
  } else if (forSale > 0 && sstc === 0 && underOffer === 0) {
    // Only For Sale properties
    label = `${forSale} FS`;
    variant = "info"; // Blue
  } else {
    // Mixed
    const agreed = [underOffer > 0 && `${underOffer} UO`, sstc > 0 && `${sstc} SSTC`].filter(Boolean);
    label = agreed.length ? `${inMailshots} (${agreed.join(", ")})` : `${inMailshots}`;
    variant = "default"; // Grey
  }

  // Get mailshot names
  const mailshotNames = mailshotStats.mailshots
    ? mailshotStats.mailshots
        .map((id) => {
          const mailshot = mailshots.find((m) => m.id === id);
          return mailshot?.name || "Unknown Mailshot";
        })
        .filter(Boolean)
    : [];

  return (
    <>
      <Badge
        variant={variant}
        size="small"
        className="mailshot-status-badge-clickable"
        onClick={(e) => {
          e.stopPropagation();
          setShowDetails(true);
        }}
      >
        <FaEnvelope style={{ marginRight: "0.25rem" }} />
        {label}
      </Badge>

      {showDetails && (
        <Modal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          title="Mailshot Status"
        >
          <div className="mailshot-status-details">
            <div className="mailshot-status-details__summary">
              <h4>Properties in Mailshots</h4>
              <div className="mailshot-status-details__stats">
                <div className="stat-row">
                  <span className="stat-label">Total Properties:</span>
                  <span className="stat-value">{totalProperties}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">In Mailshots:</span>
                  <span className="stat-value">{inMailshots}</span>
                </div>
                {forSale > 0 && (
                  <div className="stat-row">
                    <span className="stat-label">For Sale:</span>
                    <span className="stat-value">{forSale}</span>
                  </div>
                )}
                {underOffer > 0 && (
                  <div className="stat-row">
                    <span className="stat-label">Under Offer:</span>
                    <span className="stat-value">{underOffer}</span>
                  </div>
                )}
                {sstc > 0 && (
                  <div className="stat-row">
                    <span className="stat-label">Sold STC:</span>
                    <span className="stat-value">{sstc}</span>
                  </div>
                )}
              </div>
            </div>

            {mailshotNames.length > 0 && (
              <div className="mailshot-status-details__mailshots">
                <h4>Mailshots</h4>
                {loadingBreakdown ? (
                  <p style={{ textAlign: "center", color: "#9ca3af" }}>
                    Loading breakdown...
                  </p>
                ) : (
                  <ul>
                    {mailshotStats.mailshots.map((mailshotId, index) => {
                      const mailshot = mailshots.find(
                        (m) => m.id === mailshotId
                      );
                      const name = mailshot?.name || "Unknown Mailshot";
                      const stats = mailshotBreakdown[mailshotId];

                      if (!stats) return null;

                      // Build count breakdown for this specific mailshot
                      const parts = [
                        stats.forSale > 0 && `${stats.forSale} FS`,
                        stats.underOffer > 0 && `${stats.underOffer} UO`,
                        stats.sstc > 0 && `${stats.sstc} SSTC`,
                      ].filter(Boolean);
                      let countLabel = "";
                      if (parts.length > 1) {
                        countLabel = `${stats.total}: ${parts.join(", ")}`;
                      } else if (parts.length === 1) {
                        countLabel = parts[0];
                      } else if (stats.total > 0) {
                        countLabel = `${stats.total}`;
                      }

                      return (
                        <li
                          key={mailshotId}
                          onClick={() => {
                            navigate(
                              `/dashboard/properties/mailshot/${mailshotId}`
                            );
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="mailshot-name">
                            <FaEnvelope /> {name}
                          </span>
                          <span className="mailshot-count">{countLabel}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <div className="mailshot-status-details__note">
              <FaInfoCircle />
              <span>
                Properties can be added to multiple mailshots. Stats show unique
                properties across all mailshots.
              </span>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default MailshotStatusBadge;
